import React, { useEffect, useRef } from 'react';
import { loadModules } from 'esri-loader';
import { getPrecedures } from '../../../../../services/procedureService';
import Cache from '../../../../../helper/globalCacheHelper';
import useShapeStore from '../../../../../store/shapeStore';
import './ArchitecturalShapeTool.css';
import { LIST_PROCEDURE, LIST_PROCEDURE_KEY, COLORS } from './constant';
import utils from './utilities';

const ArchitecturalShapeTool = ({ view, onShapeDeleted, isActive, onToggle }) => {
  // Zustand store
  const {
    shapesData,
    selectedShapeIds,
    currentTool,
    isDrawing,
    selectedShapeType,
    selectedColor,
    shapeTypes,
    isLoadingShapeTypes,
    showLabelDialog,
    tempShape,
    shapeLabel,
    isInitialized,
    // Actions
    initializeStore,
    addShape,
    updateShape,
    removeShapes,
    setSelectedShapeIds,
    clearSelectedShapes,
    setCurrentTool,
    setIsDrawing,
    setSelectedShapeType,
    setSelectedColor,
    setShapeTypes,
    setIsLoadingShapeTypes,
    setShowLabelDialog,
    setTempShape,
    setShapeLabel,
    getShapeById,
    getSelectedShapes,
    getMeasurementSummary,
    resetDrawingState,
    forceSave,
  } = useShapeStore();

  // Refs for ArcGIS objects
  const refs = useRef({
    sketchViewModel: null,
    architecturalLayer: null,
    measurementLayer: null,
    labelGraphics: new Map(),
    measurementGraphics: new Map(),
    lineMeasurements: new Map(),
    tempMeasurements: [],
    cumulativeMeasurement: null,
    clickHandler: null,
    isRestoring: false,
    renderedShapeIds: new Set(), // Track which shapes are already rendered
  });

  // Initialize store on component mount
  useEffect(() => {
    if (!isInitialized) {
      initializeStore();
    }
  }, [isInitialized, initializeStore]);

  // Symbol creation
  const createSymbols = {
    distanceText: async (text, zoomLevel) => {
      const [TextSymbol] = await loadModules(['esri/symbols/TextSymbol']);
      const fontSize = utils.getFontSize(zoomLevel);

      if (fontSize === 0) return null;

      return new TextSymbol({
        text,
        color: [0, 0, 0, 1],
        font: {
          size: fontSize + 1,
          family: 'Arial, sans-serif',
          weight: 'bold',
        },
        haloColor: [255, 255, 255, 1],
        haloSize: 2,
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
      });
    },

    areaText: async (text) => {
      const [TextSymbol] = await loadModules(['esri/symbols/TextSymbol']);

      return new TextSymbol({
        text,
        color: [255, 255, 255, 1],
        font: {
          size: 16,
          family: 'Arial, sans-serif',
          weight: 'bold',
        },
        haloColor: [0, 0, 0, 1],
        haloSize: 3,
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
      });
    },

    cumulativeText: async (text, zoomLevel) => {
      const [TextSymbol] = await loadModules(['esri/symbols/TextSymbol']);
      const fontSize = utils.getFontSize(zoomLevel);

      if (fontSize === 0) return null;

      return new TextSymbol({
        text,
        color: [0, 150, 0, 1],
        font: {
          size: fontSize + 2,
          family: 'Arial, sans-serif',
          weight: 'bold',
        },
        backgroundColor: [255, 255, 255, 0.98],
        borderLineColor: [0, 150, 0, 1],
        borderLineSize: 2,
        haloColor: [255, 255, 255, 1],
        haloSize: 4,
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
        yoffset: -20,
      });
    },

    labelText: async (text, zoomLevel) => {
      const [TextSymbol] = await loadModules(['esri/symbols/TextSymbol']);
      const fontSize = utils.getFontSize(zoomLevel);

      if (fontSize === 0) return null;

      return new TextSymbol({
        text,
        color: [0, 0, 0, 1],
        font: {
          size: fontSize,
          family: 'Arial, sans-serif',
          weight: 'bold',
        },
        backgroundColor: [255, 255, 255, 0.95],
        borderLineColor: [0, 0, 0, 0.8],
        borderLineSize: 1,
        haloColor: [255, 255, 255, 1],
        haloSize: 2,
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
      });
    },

    shapes: async (color) => {
      const [SimpleFillSymbol, SimpleLineSymbol, SimpleMarkerSymbol] = await loadModules([
        'esri/symbols/SimpleFillSymbol',
        'esri/symbols/SimpleLineSymbol',
        'esri/symbols/SimpleMarkerSymbol',
      ]);

      const fillColor = [...color, 0.5];
      const borderColor = [...color, 1];

      return {
        polygonSymbol: new SimpleFillSymbol({
          color: fillColor,
          outline: new SimpleLineSymbol({ color: borderColor, width: 2 }),
        }),
        polylineSymbol: new SimpleLineSymbol({ color: borderColor, width: 3 }),
        pointSymbol: new SimpleMarkerSymbol({
          color: fillColor,
          size: 8,
          outline: { color: borderColor, width: 2 },
        }),
      };
    },
  };

  // Graphics creation
  const createGraphics = {
    lineMeasurement: async (coord1, coord2, distance, shapeId, segmentIndex) => {
      const [Graphic] = await loadModules(['esri/Graphic']);

      const midpoint = utils.calculateMidpoint(coord1, coord2, view);
      const measurementText = utils.formatDistance(distance);
      const textSymbol = await createSymbols.distanceText(measurementText, view.zoom);

      if (!textSymbol) return null;

      return new Graphic({
        geometry: midpoint,
        symbol: textSymbol,
        attributes: {
          isLineMeasurement: true,
          parentShapeId: shapeId,
          segmentIndex,
          distance,
          originalText: measurementText,
        },
      });
    },

    areaMeasurement: async (parentGraphic, area) => {
      const [Graphic] = await loadModules(['esri/Graphic']);

      const centroid = utils.calculateCentroid(parentGraphic.geometry);
      if (!centroid) return null;

      const measurementText = utils.formatArea(area);
      const textSymbol = await createSymbols.areaText(measurementText);

      return new Graphic({
        geometry: centroid,
        symbol: textSymbol,
        attributes: {
          isMeasurement: true,
          parentShapeId: parentGraphic.attributes.id,
          area,
        },
      });
    },

    cumulativeMeasurement: async (geometry, cumulativeValue, isArea = false) => {
      const [Graphic] = await loadModules(['esri/Graphic']);

      const centroid = utils.calculateCentroid({ type: geometry.type, ...geometry });
      if (!centroid) return null;

      const measurementText = isArea
        ? utils.formatArea(cumulativeValue)
        : utils.formatDistance(cumulativeValue);
      const textSymbol = await createSymbols.cumulativeText(measurementText, view.zoom);

      if (!textSymbol) return null;

      return new Graphic({
        geometry: centroid,
        symbol: textSymbol,
        attributes: {
          isCumulativeMeasurement: true,
          isArea,
          value: cumulativeValue,
          originalText: measurementText,
        },
      });
    },

    label: async (parentGraphic, labelText) => {
      const [Graphic] = await loadModules(['esri/Graphic']);

      const centroid = utils.calculateCentroid(parentGraphic.geometry);
      if (!centroid) return null;

      const textSymbol = await createSymbols.labelText(labelText, view.zoom);
      if (!textSymbol) return null;

      const offsetCentroid = {
        ...centroid,
        y: centroid.y + view.extent.height * 0.003,
      };

      return new Graphic({
        geometry: offsetCentroid,
        symbol: textSymbol,
        attributes: {
          isLabel: true,
          parentShapeId: parentGraphic.attributes.id,
          originalText: labelText,
          textColor: [0, 0, 0, 1],
        },
      });
    },
  };

  // Measurement operations
  const measurements = {
    calculateCumulativeValue: async (vertices, isPolygon = false) => {
      if (vertices.length < 2) return 0;

      let cumulative = 0;

      if (isPolygon && vertices.length >= 3) {
        const geometry = {
          type: 'polygon',
          rings: [vertices],
          spatialReference: view.spatialReference,
        };
        cumulative = await utils.calculateArea(geometry, view);
      } else {
        for (let i = 0; i < vertices.length - 1; i++) {
          cumulative += await utils.calculateDistance(vertices[i], vertices[i + 1], view);
        }
      }

      return cumulative;
    },

    createLineSegments: async (geometry, shapeId) => {
      const measurements = [];

      if (geometry.type === 'polygon') {
        const rings = geometry.rings[0];
        if (rings?.length > 2) {
          for (let i = 0; i < rings.length - 1; i++) {
            const distance = await utils.calculateDistance(rings[i], rings[i + 1], view);
            if (distance > 0.01) {
              const graphic = await createGraphics.lineMeasurement(
                rings[i],
                rings[i + 1],
                distance,
                shapeId,
                i
              );
              if (graphic) measurements.push(graphic);
            }
          }
        }
      } else if (geometry.type === 'polyline') {
        const paths = geometry.paths[0];
        if (paths?.length > 1) {
          for (let i = 0; i < paths.length - 1; i++) {
            const distance = await utils.calculateDistance(paths[i], paths[i + 1], view);
            if (distance > 0.01) {
              const graphic = await createGraphics.lineMeasurement(
                paths[i],
                paths[i + 1],
                distance,
                shapeId,
                i
              );
              if (graphic) measurements.push(graphic);
            }
          }
        }
      }

      return measurements;
    },

    updateTempMeasurements: async (vertices, geometryType) => {
      if (!refs.current.measurementLayer || vertices.length < 2) return;

      // Clear existing temp measurements
      refs.current.tempMeasurements.forEach((graphic) => {
        refs.current.measurementLayer.remove(graphic);
      });
      refs.current.tempMeasurements = [];

      if (refs.current.cumulativeMeasurement) {
        refs.current.measurementLayer.remove(refs.current.cumulativeMeasurement);
        refs.current.cumulativeMeasurement = null;
      }

      // Create segment measurements
      for (let i = 0; i < vertices.length - 1; i++) {
        const distance = await utils.calculateDistance(vertices[i], vertices[i + 1], view);
        if (distance > 0.01) {
          const graphic = await createGraphics.lineMeasurement(
            vertices[i],
            vertices[i + 1],
            distance,
            'temp',
            i
          );
          if (graphic) {
            refs.current.measurementLayer.add(graphic);
            refs.current.tempMeasurements.push(graphic);
          }
        }
      }

      // Create cumulative measurement
      const isPolygon = geometryType === 'polygon';
      const cumulativeValue = await measurements.calculateCumulativeValue(vertices, isPolygon);

      if (cumulativeValue > 0) {
        const geometry = {
          type: geometryType,
          [isPolygon ? 'rings' : 'paths']: [vertices],
          spatialReference: view.spatialReference,
        };

        const cumulativeGraphic = await createGraphics.cumulativeMeasurement(
          geometry,
          cumulativeValue,
          isPolygon
        );

        if (cumulativeGraphic) {
          refs.current.measurementLayer.add(cumulativeGraphic);
          refs.current.cumulativeMeasurement = cumulativeGraphic;
        }
      }
    },

    clearTemp: () => {
      if (!refs.current.measurementLayer) return;

      refs.current.tempMeasurements.forEach((graphic) => {
        refs.current.measurementLayer.remove(graphic);
      });
      refs.current.tempMeasurements = [];

      if (refs.current.cumulativeMeasurement) {
        refs.current.measurementLayer.remove(refs.current.cumulativeMeasurement);
        refs.current.cumulativeMeasurement = null;
      }
    },
  };

  // Shape management functions
  const shapeManager = {
    cleanupShapeGraphics: (shapeId) => {
      // Remove label graphic
      const labelGraphic = refs.current.labelGraphics.get(shapeId);
      if (labelGraphic) {
        refs.current.architecturalLayer.remove(labelGraphic);
        refs.current.labelGraphics.delete(shapeId);
      }

      // Remove measurement graphic
      const measurementGraphic = refs.current.measurementGraphics.get(shapeId);
      if (measurementGraphic) {
        refs.current.architecturalLayer.remove(measurementGraphic);
        refs.current.measurementGraphics.delete(shapeId);
      }

      // Remove line measurements
      const lineMeasurements = refs.current.lineMeasurements.get(shapeId);
      if (lineMeasurements?.length > 0) {
        lineMeasurements.forEach((measurement) => {
          refs.current.measurementLayer.remove(measurement);
        });
        refs.current.lineMeasurements.delete(shapeId);
      }

      // Remove from rendered set
      refs.current.renderedShapeIds.delete(shapeId);
    },

    updateShapeAfterEdit: async (graphic) => {
      if (!graphic.attributes?.id) return;

      const shapeId = graphic.attributes.id;
      const existingShape = getShapeById(shapeId);
      if (!existingShape) return;

      try {
        // Clean up old graphics first
        shapeManager.cleanupShapeGraphics(shapeId);

        // Calculate new measurements
        let area = 0;
        let cumulativeLength = 0;

        if (graphic.geometry.type === 'polygon') {
          area = await utils.calculateArea(graphic.geometry, view);
        } else if (graphic.geometry.type === 'polyline') {
          const paths = graphic.geometry.paths[0];
          if (paths?.length > 1) {
            cumulativeLength = await measurements.calculateCumulativeValue(paths, false);
          }
        }

        // Create new line measurements
        const lineMeasurements = await measurements.createLineSegments(graphic.geometry, shapeId);
        if (lineMeasurements.length > 0) {
          lineMeasurements.forEach((measurement) => {
            refs.current.measurementLayer.add(measurement);
          });
          refs.current.lineMeasurements.set(shapeId, lineMeasurements);
        }

        // Create new label
        const labelGraphic = await createGraphics.label(graphic, existingShape.label);
        if (labelGraphic) {
          refs.current.architecturalLayer.add(labelGraphic);
          refs.current.labelGraphics.set(shapeId, labelGraphic);
        }

        // Create new area/length measurement
        if (graphic.geometry.type === 'polygon' && area > 0) {
          const measurementGraphic = await createGraphics.areaMeasurement(graphic, area);
          if (measurementGraphic) {
            refs.current.architecturalLayer.add(measurementGraphic);
            refs.current.measurementGraphics.set(shapeId, measurementGraphic);
          }
        } else if (graphic.geometry.type === 'polyline' && cumulativeLength > 0) {
          const cumulativeGraphic = await createGraphics.cumulativeMeasurement(
            graphic.geometry,
            cumulativeLength,
            false
          );
          if (cumulativeGraphic) {
            refs.current.architecturalLayer.add(cumulativeGraphic);
            refs.current.measurementGraphics.set(shapeId, cumulativeGraphic);
          }
        }

        // Calculate total perimeter from line measurements
        const totalPerimeter = lineMeasurements.reduce(
          (total, m) => total + m.attributes.distance,
          0
        );

        // Update shape in store with complete data
        const updatedData = {
          geometry: graphic.geometry.toJSON(),
          coordinates: utils.extractCoordinates(graphic.geometry),
          area,
          cumulativeLength,
          totalPerimeter,
          updatedAt: new Date().toISOString(),
          lineMeasurements: lineMeasurements.map((m) => ({
            segmentIndex: m.attributes.segmentIndex,
            distance: m.attributes.distance,
            text: m.attributes.originalText,
          })),
        };

        updateShape(shapeId, updatedData);

        // Mark as rendered
        refs.current.renderedShapeIds.add(shapeId);
      } catch (error) {
        console.error('Error updating shape after edit:', error);
      }
    },

    deleteSelectedShapes: () => {
      if (selectedShapeIds.length === 0) {
        alert('Please select shapes to delete by clicking on them first.');
        return;
      }

      if (refs.current.sketchViewModel) {
        // Get selected graphics from the layer
        const selectedGraphics = [];
        refs.current.architecturalLayer.graphics.forEach((graphic) => {
          if (selectedShapeIds.includes(graphic.attributes?.id)) {
            selectedGraphics.push(graphic);
          }
        });

        if (selectedGraphics.length > 0) {
          // Clean up associated graphics for each shape
          selectedShapeIds.forEach((shapeId) => {
            shapeManager.cleanupShapeGraphics(shapeId);
          });

          // Remove main graphics from layer
          selectedGraphics.forEach((graphic) => {
            refs.current.architecturalLayer.remove(graphic);
          });

          // Remove from store
          removeShapes(selectedShapeIds);

          // Clear selection
          clearSelectedShapes();

          // Force save to ensure persistence
          forceSave();

          // Notify parent component
          if (onShapeDeleted) {
            selectedGraphics.forEach((graphic) => {
              onShapeDeleted(graphic);
            });
          }
        }
      }
    },

    restoreShapeFromStore: async (shape) => {
      if (!refs.current.architecturalLayer || !shape.geometry) {
        console.warn('Cannot restore shape - missing layer or geometry:', shape.id);
        return null;
      }

      try {
        // Check if already rendered
        if (refs.current.renderedShapeIds.has(shape.id)) {
          return null;
        }

        // Create graphic from stored geometry
        const [Graphic] = await loadModules(['esri/Graphic']);

        // Recreate geometry with current spatial reference - FIXED LINE
        const geometry = await utils.recreateGeometry(shape, view);
        if (!geometry) {
          console.error('Failed to recreate geometry for shape:', shape.id);
          return null;
        }

        // Create appropriate symbol using stored color
        const symbols = await createSymbols.shapes(shape.color);
        let symbol;
        switch (shape.tool) {
          case 'polygon':
            symbol = symbols.polygonSymbol;
            break;
          case 'polyline':
            symbol = symbols.polylineSymbol;
            break;
          case 'point':
            symbol = symbols.pointSymbol;
            break;
          default:
            symbol = symbols.polygonSymbol;
        }

        // Create main graphic with all stored attributes
        const graphic = new Graphic({
          geometry,
          symbol,
          attributes: {
            id: shape.id,
            label: shape.label,
            shapeType: shape.shapeType,
            shapeTypeLabel: shape.shapeTypeLabel,
            tool: shape.tool,
            createdAt: shape.createdAt,
            color: shape.color,
            area: shape.area,
            cumulativeLength: shape.cumulativeLength,
            totalPerimeter: shape.totalPerimeter,
            isRestoredShape: true,
          },
        });

        // Add main graphic to layer
        refs.current.architecturalLayer.add(graphic);

        // Restore label if exists
        if (shape.label) {
          const labelGraphic = await createGraphics.label(graphic, shape.label);
          if (labelGraphic) {
            refs.current.architecturalLayer.add(labelGraphic);
            refs.current.labelGraphics.set(shape.id, labelGraphic);
          }
        }

        // Restore line measurements from stored data
        if (shape.lineMeasurements?.length > 0) {
          const restoredLineMeasurements = [];

          // Get coordinates for creating measurements
          let coordinates = [];
          if (geometry.type === 'polygon' && geometry.rings?.[0]) {
            coordinates = geometry.rings[0];
          } else if (geometry.type === 'polyline' && geometry.paths?.[0]) {
            coordinates = geometry.paths[0];
          }

          // Create line measurement graphics from stored data
          for (let i = 0; i < shape.lineMeasurements.length && i < coordinates.length - 1; i++) {
            const measurement = shape.lineMeasurements[i];
            const lineMeasurementGraphic = await createGraphics.lineMeasurement(
              coordinates[i],
              coordinates[i + 1],
              measurement.distance,
              shape.id,
              measurement.segmentIndex
            );

            if (lineMeasurementGraphic) {
              refs.current.measurementLayer.add(lineMeasurementGraphic);
              restoredLineMeasurements.push(lineMeasurementGraphic);
            }
          }

          if (restoredLineMeasurements.length > 0) {
            refs.current.lineMeasurements.set(shape.id, restoredLineMeasurements);
          }
        }

        // Restore area/length measurement
        if (geometry.type === 'polygon' && shape.area > 0) {
          const measurementGraphic = await createGraphics.areaMeasurement(graphic, shape.area);
          if (measurementGraphic) {
            refs.current.architecturalLayer.add(measurementGraphic);
            refs.current.measurementGraphics.set(shape.id, measurementGraphic);
          }
        } else if (geometry.type === 'polyline' && shape.cumulativeLength > 0) {
          const cumulativeGraphic = await createGraphics.cumulativeMeasurement(
            geometry,
            shape.cumulativeLength,
            false
          );
          if (cumulativeGraphic) {
            refs.current.architecturalLayer.add(cumulativeGraphic);
            refs.current.measurementGraphics.set(shape.id, cumulativeGraphic);
          }
        }

        // Mark as rendered
        refs.current.renderedShapeIds.add(shape.id);

        return graphic;
      } catch (error) {
        console.error('Error restoring shape from store:', shape.id, error);
        return null;
      }
    },

    renderExistingShapes: async () => {
      if (!refs.current.architecturalLayer || refs.current.isRestoring) return;
      refs.current.isRestoring = true;

      try {
        // Clear all existing graphics first to avoid duplicates
        refs.current.architecturalLayer.removeAll();
        refs.current.measurementLayer.removeAll();
        refs.current.labelGraphics.clear();
        refs.current.measurementGraphics.clear();
        refs.current.lineMeasurements.clear();
        refs.current.renderedShapeIds.clear();

        // Restore each shape from store
        for (const shape of shapesData) {
          try {
            await shapeManager.restoreShapeFromStore(shape);
          } catch (error) {
            console.error('Error restoring individual shape:', shape.id, error);
          }
        }
      } catch (error) {
        console.error('Error in renderExistingShapes:', error);
      } finally {
        refs.current.isRestoring = false;
      }
    },

    clearAllShapes: () => {
      // Clear all graphics from layers
      if (refs.current.architecturalLayer) {
        refs.current.architecturalLayer.removeAll();
      }
      if (refs.current.measurementLayer) {
        refs.current.measurementLayer.removeAll();
      }

      // Clear all tracking maps
      refs.current.labelGraphics.clear();
      refs.current.measurementGraphics.clear();
      refs.current.lineMeasurements.clear();
      refs.current.renderedShapeIds.clear();
    },
  };

  // Data preparation for backend - Enhanced with complete shape data
  const prepareShapeData = (tempShape, shapeLabel, area, cumulativeLength, lineMeasurements) => {
    const shapeId = Date.now().toString();
    const coordinates = utils.extractCoordinates(tempShape.geometry);

    const totalPerimeter = lineMeasurements.reduce((total, m) => total + m.distance, 0);

    const pointsCount = (() => {
      switch (tempShape.geometry.type) {
        case 'polygon':
          return tempShape.geometry.rings[0]?.length || 0;
        case 'polyline':
          return tempShape.geometry.paths[0]?.length || 0;
        case 'point':
          return 1;
        default:
          return 0;
      }
    })();

    return {
      id: shapeId,
      label: shapeLabel.trim(),
      shapeType: selectedShapeType.id,
      shapeTypeLabel: selectedShapeType.label,
      tool: tempShape.tool,
      createdAt: new Date().toISOString(),
      color: selectedColor.color,
      geometry: tempShape.geometry.toJSON(),
      coordinates,
      pointsCount,
      area: area || 0,
      cumulativeLength: cumulativeLength || 0,
      totalPerimeter,
      lineMeasurements: lineMeasurements.map((m, index) => ({
        segmentIndex: index,
        distance: m.distance,
        text: utils.formatDistance(m.distance),
        startPoint: coordinates[0]?.[index],
        endPoint: coordinates[0]?.[index + 1],
      })),
      spatialReference: tempShape.geometry.spatialReference.toJSON(),
      extent: tempShape.geometry.extent
        ? {
            xmin: tempShape.geometry.extent.xmin,
            ymin: tempShape.geometry.extent.ymin,
            xmax: tempShape.geometry.extent.xmax,
            ymax: tempShape.geometry.extent.ymax,
          }
        : null,
      // Additional metadata for enhanced tracking
      displayValues: {
        area: area ? utils.formatArea(area) : null,
        length: cumulativeLength ? utils.formatDistance(cumulativeLength) : null,
        perimeter: totalPerimeter ? utils.formatDistance(totalPerimeter) : null,
      },
    };
  };

  // Event handlers
  const handlers = {
    updateSymbols: async () => {
      if (!refs.current.sketchViewModel || !selectedColor) return;

      const symbols = await createSymbols.shapes(selectedColor.color);
      refs.current.sketchViewModel.polygonSymbol = symbols.polygonSymbol;
      refs.current.sketchViewModel.polylineSymbol = symbols.polylineSymbol;
      refs.current.sketchViewModel.pointSymbol = symbols.pointSymbol;
    },

    startDrawing: (tool) => {
      if (refs.current.sketchViewModel && selectedShapeType && selectedColor) {
        refs.current.sketchViewModel.cancel();
        measurements.clearTemp();
        handlers.updateSymbols();
        refs.current.sketchViewModel.create(tool);

        setCurrentTool(tool);
        setIsDrawing(true);
      }
    },

    cancelDrawing: () => {
      if (refs.current.sketchViewModel) {
        refs.current.sketchViewModel.cancel();
        measurements.clearTemp();
        resetDrawingState();
      }
    },

    saveLabeledShape: async () => {
      if (!tempShape || !shapeLabel.trim()) {
        alert('Please enter a label for the shape.');
        return;
      }

      try {
        // Update graphic symbol with selected color
        const symbols = await createSymbols.shapes(selectedColor.color);

        if (tempShape.tool === 'polygon') {
          tempShape.graphic.symbol = symbols.polygonSymbol;
        } else if (tempShape.tool === 'polyline') {
          tempShape.graphic.symbol = symbols.polylineSymbol;
        } else if (tempShape.tool === 'point') {
          tempShape.graphic.symbol = symbols.pointSymbol;
        }

        // Calculate measurements
        let area = 0;
        let cumulativeLength = 0;

        if (tempShape.geometry.type === 'polygon') {
          area = await utils.calculateArea(tempShape.geometry, view);
        } else if (tempShape.geometry.type === 'polyline') {
          const paths = tempShape.geometry.paths[0];
          if (paths?.length > 1) {
            cumulativeLength = await measurements.calculateCumulativeValue(paths, false);
          }
        }

        // Prepare complete shape data
        const shapeData = prepareShapeData(tempShape, shapeLabel, area, cumulativeLength, []);

        // Add attributes to graphic
        tempShape.graphic.attributes = {
          ...tempShape.graphic.attributes,
          ...shapeData,
        };

        // Create and add label
        const labelGraphic = await createGraphics.label(tempShape.graphic, shapeLabel);
        if (labelGraphic) {
          refs.current.architecturalLayer.add(labelGraphic);
          refs.current.labelGraphics.set(shapeData.id, labelGraphic);
        }

        // Create line measurements
        const lineMeasurements = await measurements.createLineSegments(
          tempShape.geometry,
          shapeData.id
        );
        if (lineMeasurements.length > 0) {
          lineMeasurements.forEach((measurement) => {
            refs.current.measurementLayer.add(measurement);
          });
          refs.current.lineMeasurements.set(shapeData.id, lineMeasurements);

          // Update shape data with actual measurements
          shapeData.lineMeasurements = lineMeasurements.map((m) => ({
            segmentIndex: m.attributes.segmentIndex,
            distance: m.attributes.distance,
            text: m.attributes.originalText,
          }));
          shapeData.totalPerimeter = lineMeasurements.reduce(
            (total, m) => total + m.attributes.distance,
            0
          );
        }

        // Create area measurement for polygons
        if (tempShape.geometry.type === 'polygon' && area > 0) {
          const measurementGraphic = await createGraphics.areaMeasurement(tempShape.graphic, area);
          if (measurementGraphic) {
            refs.current.architecturalLayer.add(measurementGraphic);
            refs.current.measurementGraphics.set(shapeData.id, measurementGraphic);
          }
        } else if (tempShape.geometry.type === 'polyline' && cumulativeLength > 0) {
          const cumulativeGraphic = await createGraphics.cumulativeMeasurement(
            tempShape.geometry,
            cumulativeLength,
            false
          );
          if (cumulativeGraphic) {
            refs.current.architecturalLayer.add(cumulativeGraphic);
            refs.current.measurementGraphics.set(shapeData.id, cumulativeGraphic);
          }
        }

        // Add to store (this will automatically save to sessionStorage)
        addShape(shapeData);

        // Mark as rendered
        refs.current.renderedShapeIds.add(shapeData.id);

        // Reset drawing state
        resetDrawingState();
      } catch (error) {
        console.error('Error saving shape:', error);
        alert('Error saving shape. Please try again.');
      }
    },

    saveAllToDatabase: async () => {
      if (shapesData.length === 0) {
        alert('No shapes to save.');
        return;
      }

      try {
        const projectData = {
          project: {
            id: `project_${Date.now()}`,
            name: 'Architectural Development Plan',
            createdAt: new Date().toISOString(),
            totalShapes: shapesData.length,
            spatialReference: view.spatialReference.toJSON(),
            mapExtent: view.extent.toJSON(),
          },
          shapes: shapesData.map((shape) => ({
            id: shape.id,
            label: shape.label,
            shapeType: shape.shapeType,
            shapeTypeLabel: shape.shapeTypeLabel,
            tool: shape.tool,
            createdAt: shape.createdAt,
            updatedAt: shape.updatedAt || shape.createdAt,
            color: shape.color,
            geometry: shape.geometry,
            coordinates: shape.coordinates,
            pointsCount: shape.pointsCount,
            spatialReference: shape.spatialReference,
            extent: shape.extent,
            measurements: {
              area: shape.area,
              cumulativeLength: shape.cumulativeLength,
              totalPerimeter: shape.totalPerimeter,
              lineSegments:
                shape.lineMeasurements?.map((segment) => ({
                  index: segment.segmentIndex,
                  distance: segment.distance,
                  distanceFormatted: segment.text,
                  startCoordinate: segment.startPoint,
                  endCoordinate: segment.endPoint,
                })) || [],
            },
            displayValues: shape.displayValues || {
              area: shape.area ? utils.formatArea(shape.area) : null,
              length: shape.cumulativeLength ? utils.formatDistance(shape.cumulativeLength) : null,
              perimeter: shape.totalPerimeter ? utils.formatDistance(shape.totalPerimeter) : null,
            },
          })),
          summary: getMeasurementSummary(),
          sessionData: {
            lastSaved: new Date().toISOString(),
            storageStats: useShapeStore.getState().getStorageStats?.() || null,
          },
        };

        // Store in memory for demo (replace with actual API call)
        window.architecturalProjectData = projectData;

        alert(
          `Successfully prepared ${projectData.shapes.length} shapes with complete measurements for database!`
        );

        // Force save to ensure all data is persisted
        forceSave();
      } catch (error) {
        console.error('Error preparing data for database:', error);
        alert('Error preparing data for database.');
      }
    },

    clearAllShapes: () => {
      if (
        window.confirm('Are you sure you want to clear all shapes? This action cannot be undone.')
      ) {
        // Clear from store (this will also clear sessionStorage)
        useShapeStore.getState().resetShapesOnly();

        // Clear from map
        shapeManager.clearAllShapes();

        // Clear selection
        clearSelectedShapes();
      }
    },
  };

  // Fetch shape types from API - Enhanced to restore from store
  useEffect(() => {
    const fetchShapeTypes = async () => {
      if (!isActive || !isInitialized) return;

      // Try to restore from store first
      if (shapeTypes.length > 0 && selectedShapeType && selectedColor) {
        return;
      }

      if (Cache.hasCache(LIST_PROCEDURE, LIST_PROCEDURE_KEY)) {
        const response = Cache.getCache(LIST_PROCEDURE, LIST_PROCEDURE_KEY);
        const transformedTypes = response.map((type, index) => ({
          id: type._id,
          label: type.procedureName,
          color: utils.getShapeTypeColor(index),
          originalData: type,
        }));

        setShapeTypes(transformedTypes);
        if (!selectedShapeType) setSelectedShapeType(transformedTypes[0] || null);
        if (!selectedColor) setSelectedColor(COLORS[0]);
        return;
      }

      try {
        setIsLoadingShapeTypes(true);
        const response = await getPrecedures();
        Cache.setCache(LIST_PROCEDURE, LIST_PROCEDURE_KEY, response);

        const transformedTypes = response.map((type, index) => ({
          id: type._id,
          label: type.procedureName,
          color: utils.getShapeTypeColor(index),
          originalData: type,
        }));

        setShapeTypes(transformedTypes);
        if (!selectedShapeType) setSelectedShapeType(transformedTypes[0] || null);
        if (!selectedColor) setSelectedColor(COLORS[0]);
        setIsLoadingShapeTypes(false);
      } catch (error) {
        console.error('Error fetching shape types:', error);

        const fallbackTypes = [
          { id: 'building', label: 'Building/House', color: [255, 165, 0] },
          { id: 'pool', label: 'Swimming Pool', color: [0, 191, 255] },
          { id: 'garage', label: 'Garage/Carport', color: [128, 128, 128] },
          { id: 'garden', label: 'Garden Area', color: [34, 139, 34] },
          { id: 'driveway', label: 'Driveway', color: [105, 105, 105] },
          { id: 'patio', label: 'Patio/Deck', color: [160, 82, 45] },
          { id: 'shed', label: 'Shed/Storage', color: [139, 69, 19] },
          { id: 'boundary', label: 'Property Boundary', color: [255, 0, 0] },
          { id: 'setback', label: 'Setback Line', color: [255, 255, 0] },
          { id: 'other', label: 'Other Structure', color: [128, 0, 128] },
        ];

        setShapeTypes(fallbackTypes);
        if (!selectedShapeType) setSelectedShapeType(fallbackTypes[0]);
        if (!selectedColor) setSelectedColor(COLORS[0]);
        setIsLoadingShapeTypes(false);
      }
    };

    fetchShapeTypes();
  }, [isActive, isInitialized, shapeTypes.length, selectedShapeType, selectedColor]);

  // Initialize sketch tools and render existing shapes - Enhanced
  useEffect(() => {
    if (!view || !isActive || !selectedShapeType || !selectedColor || !isInitialized) return;

    let sketchViewModel;

    const initialize = async () => {
      try {
        const [SketchViewModel, GraphicsLayer] = await loadModules([
          'esri/widgets/Sketch/SketchViewModel',
          'esri/layers/GraphicsLayer',
        ]);

        await view.when();

        // Create or get architectural layer
        let architecturalLayer = view.map.findLayerById('architectural-shapes');
        if (!architecturalLayer) {
          architecturalLayer = new GraphicsLayer({
            id: 'architectural-shapes',
            title: 'Architectural Shapes',
            listMode: 'hide',
          });
          view.map.add(architecturalLayer);
        }
        refs.current.architecturalLayer = architecturalLayer;

        // Create or get measurement layer
        let measurementLayer = view.map.findLayerById('shape-measurements');
        if (!measurementLayer) {
          measurementLayer = new GraphicsLayer({
            id: 'shape-measurements',
            title: 'Shape Measurements',
            listMode: 'hide',
          });
          view.map.add(measurementLayer);
        }
        refs.current.measurementLayer = measurementLayer;

        // Create symbols
        const symbols = await createSymbols.shapes(selectedColor.color);

        // Create SketchViewModel
        sketchViewModel = new SketchViewModel({
          view,
          layer: architecturalLayer,
          updateOnGraphicClick: true,
          defaultUpdateOptions: {
            tool: 'reshape',
            enableRotation: true,
            enableScaling: true,
            preserveAspectRatio: false,
            multipleSelectionEnabled: true,
          },
          polygonSymbol: symbols.polygonSymbol,
          polylineSymbol: symbols.polylineSymbol,
          pointSymbol: symbols.pointSymbol,
        });

        refs.current.sketchViewModel = sketchViewModel;

        // Handle create events
        sketchViewModel.on('create', async (event) => {
          if (event.state === 'start') {
            setIsDrawing(true);
            measurements.clearTemp();
          }

          if (event.state === 'active' && event.graphic?.geometry) {
            const geometry = event.graphic.geometry;
            if (geometry.type === 'polygon' && geometry.rings?.[0]) {
              await measurements.updateTempMeasurements(geometry.rings[0], 'polygon');
            } else if (geometry.type === 'polyline' && geometry.paths?.[0]) {
              await measurements.updateTempMeasurements(geometry.paths[0], 'polyline');
            }
          }

          if (event.state === 'complete') {
            setTempShape({
              graphic: event.graphic,
              tool: event.tool,
              geometry: event.graphic.geometry,
            });
            setShowLabelDialog(true);
            setIsDrawing(false);
            measurements.clearTemp();
          }

          if (event.state === 'cancel') {
            setIsDrawing(false);
            measurements.clearTemp();
          }
        });

        // Handle update events - Enhanced with store updates
        sketchViewModel.on('update', async (event) => {
          if (event.state === 'complete' && event.graphics.length > 0) {
            for (const graphic of event.graphics) {
              await shapeManager.updateShapeAfterEdit(graphic);
            }
            // Force save after updates
            forceSave();
          }
        });

        // Handle delete events - Enhanced with store sync
        sketchViewModel.on('delete', (event) => {
          const deletedShapeIds = [];

          event.graphics.forEach((graphic) => {
            if (graphic.attributes?.id) {
              const shapeId = graphic.attributes.id;
              deletedShapeIds.push(shapeId);
              shapeManager.cleanupShapeGraphics(shapeId);
            }

            if (onShapeDeleted) {
              onShapeDeleted(graphic);
            }
          });

          if (deletedShapeIds.length > 0) {
            removeShapes(deletedShapeIds);
            forceSave(); // Force save after deletions
          }
        });

        // Handle click for selection
        if (refs.current.clickHandler) {
          refs.current.clickHandler.remove();
        }

        refs.current.clickHandler = view.on('click', (evt) => {
          if (isDrawing) return;

          view.hitTest(evt).then((res) => {
            const graphics = res.results
              .filter(
                (r) =>
                  r.layer === architecturalLayer &&
                  r.graphic.attributes &&
                  !r.graphic.attributes.isLabel &&
                  !r.graphic.attributes.isMeasurement &&
                  !r.graphic.attributes.isLineMeasurement &&
                  !r.graphic.attributes.isCumulativeMeasurement
              )
              .map((r) => r.graphic);

            if (graphics.length > 0) {
              const graphicIds = graphics.map((g) => g.attributes.id).filter(Boolean);
              setSelectedShapeIds(graphicIds);
              sketchViewModel.update(graphics, { tool: 'transform' });
            } else {
              clearSelectedShapes();
            }
          });
        });

        await shapeManager.renderExistingShapes();
      } catch (error) {
        console.error('Error initializing sketch tools:', error);
      }
    };

    initialize();

    return () => {
      if (refs.current.clickHandler) {
        refs.current.clickHandler.remove();
      }
      if (sketchViewModel) {
        sketchViewModel.destroy();
      }
      clearSelectedShapes();
      measurements.clearTemp();
    };
  }, [view, isActive, selectedShapeType, selectedColor, isInitialized]);

  // Watch for changes in shapesData and re-render when needed
  useEffect(() => {
    if (refs.current.architecturalLayer && isInitialized && !refs.current.isRestoring) {
      // Only re-render if there are changes
      const currentRenderedCount = refs.current.renderedShapeIds.size;
      const storeShapeCount = shapesData.length;

      if (currentRenderedCount !== storeShapeCount) {
        shapeManager.renderExistingShapes();
      }
    }
  }, [shapesData.length, isInitialized]);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (shapesData.length > 0) {
        forceSave();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [shapesData.length, forceSave]);

  if (!isActive) return null;

  const measurementsSummary = getMeasurementSummary();

  return (
    <div className="architectural-tool-container">
      {/* Header */}
      <div className="tool-header">
        <h3 className="tool-title">🏗️ Development Planner</h3>
        <button onClick={onToggle} className="close-button" type="button">
          ✕
        </button>
      </div>

      {/* Shape Type Selection */}
      <div className="section">
        <label className="section-label" id="section">
          Structure Type:
        </label>
        {isLoadingShapeTypes ? (
          <div className="loading-container">Loading structure types...</div>
        ) : (
          <select
            value={selectedShapeType?.id || ''}
            onChange={(e) => {
              const shapeType = shapeTypes.find((st) => st.id === e.target.value);
              setSelectedShapeType(shapeType);
            }}
            disabled={shapeTypes.length === 0}
            className="select-dropdown"
          >
            {shapeTypes.length === 0 ? (
              <option>No structure types available</option>
            ) : (
              shapeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))
            )}
          </select>
        )}
      </div>

      {/* Color Selection */}
      <div className="section">
        <label className="section-label">Shape Color:</label>
        <div className="color-selection">
          <div className="color-grid">
            {COLORS.map((color) => (
              <div
                key={color.id}
                className={`color-option ${selectedColor?.id === color.id ? 'selected' : ''}`}
                style={{ backgroundColor: `rgba(${color.color.join(',')}, 0.8)` }}
                onClick={() => setSelectedColor(color)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedColor(color);
                  }
                }}
                role="button"
                tabIndex={0}
                title={color.name}
                aria-label={`Select ${color.name} color`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Drawing Tools */}
      <div className="section">
        <label className="section-label">Drawing Tools:</label>
        <div className="tools-grid">
          {[
            { tool: 'polygon', icon: '🖊️', label: 'Polygon' },
            { tool: 'polyline', icon: '📏', label: 'Line' },
            { tool: 'point', icon: '📍', label: 'Point' },
          ].map(({ tool, icon, label }) => (
            <button
              key={tool}
              onClick={() => handlers.startDrawing(tool)}
              disabled={isDrawing || !selectedShapeType || !selectedColor || isLoadingShapeTypes}
              className={`tool-button ${currentTool === tool ? 'active' : ''}`}
              type="button"
            >
              <span>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {selectedShapeIds.length > 0 && (
          <div className="selection-info">
            💡 Selected shapes can be moved, rotated, and resized. Click and drag to move!
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="control-buttons">
        <button
          onClick={handlers.cancelDrawing}
          disabled={!isDrawing}
          className={`control-button cancel ${isDrawing ? 'active' : ''}`}
          type="button"
        >
          ⏸️ Cancel Drawing
        </button>

        <button
          onClick={shapeManager.deleteSelectedShapes}
          disabled={selectedShapeIds.length === 0}
          className={`control-button delete ${selectedShapeIds.length > 0 ? 'active' : ''}`}
          type="button"
        >
          🗑️ Delete Selected ({selectedShapeIds.length})
        </button>

        <button
          onClick={handlers.clearAllShapes}
          disabled={shapesData.length === 0}
          className={`control-button delete ${shapesData.length > 0 ? 'active' : ''}`}
          type="button"
        >
          🗑️ Clear All ({shapesData.length})
        </button>
      </div>

      {/* Summary Display */}
      <div className="summary-section">
        <label className="section-label">Project Summary:</label>
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Shapes:</span>
            <span className="stat-value">{measurementsSummary.totalShapes}</span>
          </div>
          {measurementsSummary.totalArea > 0 && (
            <div className="stat-item">
              <span className="stat-label">Total Area:</span>
              <span className="stat-value">{utils.formatArea(measurementsSummary.totalArea)}</span>
            </div>
          )}
          {measurementsSummary.totalLength > 0 && (
            <div className="stat-item">
              <span className="stat-label">Total Length:</span>
              <span className="stat-value">
                {utils.formatDistance(measurementsSummary.totalLength)}
              </span>
            </div>
          )}
          <div className="stat-item">
            <span className="stat-label">Session Storage:</span>
            <span className="stat-value">✅ Auto-Save Active</span>
          </div>
        </div>
      </div>
      {/* Database Operations */}
      <div className="database-section">
        <label className="section-label">Database Operations:</label>
        <button
          onClick={handlers.saveAllToDatabase}
          disabled={shapesData.length === 0}
          className={`save-button ${shapesData.length > 0 ? 'active' : ''}`}
          type="button"
        >
          💾 Save All to DB ({shapesData.length})
        </button>
      </div>

      {/* Drawing Status */}
      {/* {isDrawing && (
        <div className="drawing-status">
          🎯 Drawing {currentTool} for {selectedShapeType?.label}
          <br />
          <small className="drawing-status-small">
            📏 Clean distance labels + cumulative {currentTool === 'polygon' ? 'area' : 'length'} in
            real-time
            <br />
            {currentTool === 'polygon'
              ? 'Click to add points • Double-click to finish • Green = Total Area'
              : currentTool === 'polyline'
                ? 'Click to add points • Double-click to finish • Green = Total Length'
                : 'Click on map to place point'}{' '}
            • ESC to cancel
          </small>
        </div>
      )} */}

      {/* Label Dialog */}
      {showLabelDialog && (
        <div className="dialog-overlay">
          <div className="dialog-content">
            <h4 className="dialog-title">🏷️ Label Your {selectedShapeType?.label}</h4>

            <div className="dialog-info">
              💡 Labels automatically adjust size based on zoom level and appear at the center of
              your shape.
              <br />
              📏 Orange labels show individual line segment measurements (clean display).
              <br />
              🟢 Green labels show cumulative{' '}
              {tempShape?.geometry?.type === 'polygon' ? 'area' : 'length'} for the entire shape.
              {tempShape?.geometry?.type === 'polygon' && (
                <>
                  <br />
                  📐 Total area and perimeter calculations are automatically computed.
                </>
              )}
            </div>

            <input
              type="text"
              placeholder={`e.g., "Main ${selectedShapeType?.label}"`}
              value={shapeLabel}
              onChange={(e) => setShapeLabel(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlers.saveLabeledShape();
                }
              }}
              className="dialog-input"
              autoFocus
            />

            <div className="dialog-buttons">
              <button
                onClick={() => {
                  if (tempShape?.graphic && refs.current.architecturalLayer) {
                    refs.current.architecturalLayer.remove(tempShape.graphic);
                  }
                  resetDrawingState();
                }}
                className="dialog-button cancel"
                type="button"
              >
                Cancel
              </button>

              <button
                onClick={handlers.saveLabeledShape}
                disabled={!shapeLabel.trim()}
                className="dialog-button save"
                type="button"
              >
                💾 Save Shape with Measurements
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchitecturalShapeTool;
